import React, { useState } from 'react';
import {
  Zap,
  TrendingUp,
  PauseCircle,
  PlayCircle,
  DollarSign,
  Target,
  ChevronDown,
  ChevronUp,
  Info,
  Sparkles,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { DashboardRecommendation } from '../../../shared/types/dashboard';

interface SmartRecommendationsProps {
  recommendations: DashboardRecommendation[];
  onExecute: (recommendation: DashboardRecommendation) => Promise<void>;
  isLoading?: boolean;
  maxVisible?: number;
}

export const SmartRecommendations: React.FC<SmartRecommendationsProps> = ({
  recommendations,
  onExecute,
  isLoading = false,
  maxVisible = 3
}) => {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [executingAction, setExecutingAction] = useState<string | null>(null);
  const [executedActions, setExecutedActions] = useState<Set<string>>(new Set());

  const visibleRecommendations = recommendations.slice(0, maxVisible);

  const getTypeIcon = (type: DashboardRecommendation['type']) => {
    switch (type) {
      case 'scale':
        return <TrendingUp className="h-5 w-5" />;
      case 'pause':
        return <PauseCircle className="h-5 w-5" />;
      case 'launch':
        return <PlayCircle className="h-5 w-5" />;
      case 'optimize':
        return <Target className="h-5 w-5" />;
      case 'reallocate':
        return <DollarSign className="h-5 w-5" />;
      default:
        return <Zap className="h-5 w-5" />;
    }
  };

  const getTypeColor = (type: DashboardRecommendation['type']) => {
    switch (type) {
      case 'scale':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      case 'pause':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
      case 'launch':
        return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30';
      case 'optimize':
        return 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30';
      case 'reallocate':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/30';
    }
  };

  const getPriorityBadge = (priority: number) => {
    switch (priority) {
      case 1:
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 2:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 3:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-green-600 dark:text-green-400';
    if (confidence >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getActionButtonColor = (type: DashboardRecommendation['type']) => {
    switch (type) {
      case 'scale':
      case 'launch':
        return 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
      case 'pause':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'optimize':
        return 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500';
      case 'reallocate':
        return 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500';
      default:
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
    }
  };

  const handleExecute = async (recommendation: DashboardRecommendation) => {
    setExecutingAction(recommendation.id);
    try {
      await onExecute(recommendation);
      setExecutedActions(prev => new Set(prev).add(recommendation.id));
    } catch (error) {
      console.error('Failed to execute recommendation:', error);
    } finally {
      setExecutingAction(null);
    }
  };

  const formatDataSource = (source: string) => {
    const sourceMap: Record<string, string> = {
      'meta_ads': 'Meta Ads',
      'spotify': 'Spotify',
      'historical': 'Historical Data',
      'ai_analysis': 'AI Analysis',
      'budget_system': 'Budget System'
    };
    return sourceMap[source] || source;
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-100 dark:bg-gray-600 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="text-center py-8">
          <Sparkles className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No recommendations available
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            We'll analyze your campaign performance and suggest optimizations soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-yellow-500" />
          Smart Recommendations
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          AI-powered actions to optimize your campaigns
        </p>
      </div>

      <div className="space-y-4">
        {visibleRecommendations.map((recommendation) => {
          const isExpanded = expandedCard === recommendation.id;
          const isExecuting = executingAction === recommendation.id;
          const isExecuted = executedActions.has(recommendation.id);

          return (
            <div
              key={recommendation.id}
              className={`
                border rounded-lg p-4 transition-all duration-200
                ${isExecuted 
                  ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10' 
                  : 'border-gray-200 dark:border-gray-700 hover:shadow-md'
                }
              `}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`p-2 rounded-lg ${getTypeColor(recommendation.type)}`}>
                    {getTypeIcon(recommendation.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPriorityBadge(recommendation.priority)}`}>
                        Priority {recommendation.priority}
                      </span>
                      <span className={`text-xs font-medium ${getConfidenceColor(recommendation.confidence)}`}>
                        {recommendation.confidence}% confidence
                      </span>
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {recommendation.title}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {recommendation.reasoning}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Impact:</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {recommendation.impact.display}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setExpandedCard(isExpanded ? null : recommendation.id)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1"
                  >
                    <Info className="h-3 w-3" />
                    {isExpanded ? 'Less' : 'Why?'}
                    {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                </div>

                <button
                  onClick={() => handleExecute(recommendation)}
                  disabled={isExecuting || isExecuted}
                  className={`
                    px-4 py-2 rounded-md text-sm font-medium text-white
                    transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                    focus:outline-none focus:ring-2 focus:ring-offset-2
                    ${isExecuted 
                      ? 'bg-green-600' 
                      : getActionButtonColor(recommendation.type)
                    }
                  `}
                >
                  {isExecuting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Executing...
                    </span>
                  ) : isExecuted ? (
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Executed
                    </span>
                  ) : (
                    recommendation.action.label
                  )}
                </button>
              </div>

              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {recommendation.details && (
                    <div className="mb-3">
                      <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                        Detailed Analysis
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                        {recommendation.details}
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Action Type:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {recommendation.action.type.replace('_', ' ')}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Timeframe:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {recommendation.impact.timeframe}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Data Sources:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {recommendation.data_sources.map((source) => (
                        <span
                          key={source}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        >
                          {formatDataSource(source)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {recommendations.length > maxVisible && (
        <div className="mt-4 text-center">
          <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
            View {recommendations.length - maxVisible} more recommendations
          </button>
        </div>
      )}
    </div>
  );
};
